// src/components/surveys/questions/NumberQuestion.tsx
import React from 'react';
import { SurveyQuestion } from '../../../types/survey.types';
import { Hash, Minus, Plus } from 'lucide-react';

interface NumberQuestionProps {
  question: SurveyQuestion;
  value?: number | null;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
  showValidation?: boolean;
  error?: string;
  mode?: 'edit' | 'preview' | 'response';
}

const NumberQuestion: React.FC<NumberQuestionProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  showValidation = false,
  error,
  mode = 'response',
}) => {
  const minValue = question.validation?.min;
  const maxValue = question.validation?.max;
  const step = question.validation?.step || 1;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      const numValue = e.target.value === '' ? null : parseFloat(e.target.value);
      onChange(numValue);
    }
  };

  const handleIncrement = () => {
    if (disabled || !onChange) return;
    const newValue = (value || 0) + step;
    if (maxValue !== undefined && newValue > maxValue) return;
    onChange(newValue);
  };

  const handleDecrement = () => {
    if (disabled || !onChange) return;
    const newValue = (value || 0) - step;
    if (minValue !== undefined && newValue < minValue) return;
    onChange(newValue);
  };

  if (mode === 'edit') {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500">Number input preview:</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="p-3 border border-gray-200 rounded-lg bg-gray-50"
            disabled
          >
            <Minus className="h-4 w-4 text-gray-400" />
          </button>
          <input
            type="number"
            className="flex-1 p-3 border border-gray-200 rounded-lg bg-gray-50 text-center"
            placeholder="0"
            disabled
          />
          <button
            type="button"
            className="p-3 border border-gray-200 rounded-lg bg-gray-50"
            disabled
          >
            <Plus className="h-4 w-4 text-gray-400" />
          </button>
        </div>
        <div className="text-xs text-gray-400">
          {minValue !== undefined && `Min: ${minValue}`}
          {minValue !== undefined && maxValue !== undefined && ' | '}
          {maxValue !== undefined && `Max: ${maxValue}`}
          {step !== 1 && ` | Step: ${step}`}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={disabled || (minValue !== undefined && (value || 0) <= minValue)}
          className={`p-3 border-2 rounded-lg transition-colors ${
            disabled
              ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
              : 'bg-white border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
          }`}
        >
          <Minus className="h-4 w-4 text-gray-600" />
        </button>
        <input
          type="number"
          value={value ?? ''}
          onChange={handleChange}
          disabled={disabled}
          min={minValue}
          max={maxValue}
          step={step}
          placeholder="Enter a number"
          className={`flex-1 p-3 border-2 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            error
              ? 'border-red-300 focus:border-red-500'
              : value !== null && value !== undefined
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-200'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
        />
        <button
          type="button"
          onClick={handleIncrement}
          disabled={disabled || (maxValue !== undefined && (value || 0) >= maxValue)}
          className={`p-3 border-2 rounded-lg transition-colors ${
            disabled
              ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
              : 'bg-white border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
          }`}
        >
          <Plus className="h-4 w-4 text-gray-600" />
        </button>
      </div>
      {(minValue !== undefined || maxValue !== undefined) && (
        <div className="text-xs text-gray-400 text-center">
          {minValue !== undefined && `Min: ${minValue}`}
          {minValue !== undefined && maxValue !== undefined && ' - '}
          {maxValue !== undefined && `Max: ${maxValue}`}
        </div>
      )}
      {showValidation && error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default NumberQuestion;
